import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';

import { useMutation, useQuery } from '@tanstack/react-query';
import FeatherIcon from 'feather-icons-react';
import { ORDER_TYPE, OrderItemsDoc } from 'khufu-legos';
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  FormControl,
  InputGroup,
  Nav,
  ProgressBar,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';

import { getProducts, orderPrepare } from '../../../apis/queries';
import EmptyTableState from '../../../components/EmptyState/EmptyStateTable';
import Loader from '../../../components/Loader';
import { Customer } from '../../../types';
import { CART_KEY, PREPARED_ORDER_KEY } from '../../../utils/tenant';
import { PRODUCTS_QUERY_KEY } from '../../Products/Products';

type CartItem = Omit<OrderItemsDoc, '_id'> & { _id: string; sellingPrice?: number };
type CartState = { [key: string]: CartItem };

type OrderPrepareItemsInput = {
  productId: string;
  quantity: number;
  price?: number;
};

type CreateOrderPrepareRequestBody = { items: OrderPrepareItemsInput[]; type: string };

interface StepTwoParams {
  setActiveStep: (step: number) => void;
  activeStep: number;
  customer: Customer;
  resetCustomerSelection: () => void;
  orderType: ORDER_TYPE;
}

const StepTwo = ({ setActiveStep, activeStep, customer, resetCustomerSelection, orderType }: StepTwoParams) => {
  const { data, isLoading } = useQuery({
    queryKey: PRODUCTS_QUERY_KEY,
    queryFn: () => getProducts({ isActive: true })
  });
  const [isDiscountEnabled, setIsDiscountEnabled] = useState(false);
  const products = data?.results;

  const mutation = useMutation({
    mutationFn: (data: CreateOrderPrepareRequestBody) => {
      return orderPrepare(String(customer._id), {
        items: data.items,
        type: data.type
      });
    },
    onSuccess: data => {
      if (data?.data) {
        setActiveStep(activeStep + 1);
        localStorage.setItem(PREPARED_ORDER_KEY, JSON.stringify(data?.data));
      } else {
        console.error('Order data is not available');
      }
    }
  });

  const mutationLoading = mutation?.isPending;

  // An order always starts fresh: nothing from the customer's earlier returns is
  // carried in. The cart is either the one saved mid-flow or empty.
  const savedCart = localStorage.getItem(CART_KEY);
  const prevSavedCart: CartState = savedCart ? JSON.parse(savedCart) : null;
  const [cart, setCart] = useState<CartState>(prevSavedCart ?? {});

  // State for selling prices (defaults to product prices)
  const [sellingPrices, setSellingPrices] = useState<Record<string, number>>(() => {
    // Initialize with product prices if available
    return (
      products?.reduce(
        (acc, product) => {
          acc[product._id.toString()] = product.price ?? 0;
          return acc;
        },
        {} as Record<string, number>
      ) ?? {}
    );
  });

  useEffect(() => {
    // Sync cart with localStorage whenever the cart state changes
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  if (isLoading || !products) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Loader />
      </div>
    );
  }

  const handleSellingPriceChange = (id: string, value: string) => {
    const originalProduct = products.find(p => p._id.toString() === id);
    if (!originalProduct) return;

    const newPrice = parseFloat(value) || 0;
    const maxAllowedPrice = originalProduct.price ?? 0;

    if (newPrice > maxAllowedPrice) {
      // Reset to original price
      setSellingPrices(prev => ({
        ...prev,
        [id]: maxAllowedPrice
      }));
      return;
    }

    setSellingPrices(prev => ({
      ...prev,
      [id]: newPrice
    }));

    setCart(prevCart => {
      if (!prevCart[id]) return prevCart;

      return {
        ...prevCart,
        [id]: {
          ...prevCart[id],
          sellingPrice: newPrice
        }
      };
    });
  };

  const handleQuantityChange = (id: string, value: number) => {
    const originalProduct = products.find(p => p._id.toString() === id);

    setCart(prevCart => {
      const existingCartItem = prevCart[id];
      const product = existingCartItem || originalProduct;
      if (!product) return prevCart;

      const newQuantity = Math.max(0, Math.min(value, originalProduct?.stockAtHand ?? 0));

      if (newQuantity === 0) {
        // Automatically remove product from cart when the selected quantity is zero
        const { [id]: _, ...newCart } = prevCart;
        _;
        return newCart;
      }

      return {
        ...prevCart,
        [id]: {
          ...(existingCartItem || originalProduct),
          _id: id,
          quantity: newQuantity,
          sellingPrice: sellingPrices[id]
        }
      };
    });
  };

  const incrementQuantity = (id: string) => {
    handleQuantityChange(id, (cart[id]?.quantity || 0) + 1);
  };

  const decrementQuantity = (id: string) => {
    handleQuantityChange(id, (cart[id]?.quantity || 0) - 1);
  };

  const handleRemoveItem = (id: string) => {
    setCart(prevCart => {
      const newCart = { ...prevCart };
      delete newCart[id];
      return newCart;
    });
  };

  const handleSubmit = async () => {
    const finalPreparedItems: OrderPrepareItemsInput[] = [];

    for (const productId in cart) {
      const item = cart[productId];

      const preparedItem: OrderPrepareItemsInput = {
        productId,
        quantity: item.quantity
      };

      const originalProduct = products.find(p => p._id.toString() === productId);

      if (item.sellingPrice !== originalProduct?.price) {
        preparedItem.price = item.sellingPrice;
      }

      finalPreparedItems.push(preparedItem);
    }

    await mutation.mutate({ items: finalPreparedItems, type: orderType });
  };

  const handleToggleDiscount = () => {
    setIsDiscountEnabled(prev => !prev);
  };

  const totalProductsInCart = Object.keys(cart).length;

  return (
    <div className="main-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12} lg={12} xl={12} className="py-6">
            <Row className="justify-content-center">
              <Col xs={12} md={10} lg={8} xl={6} className="text-center">
                <h6 className="mb-4 text-uppercase text-muted">Step {activeStep} of 3</h6>
                <h3 className="mb-4">Customer: {customer.name}</h3>
                <Col>
                  <ProgressBar variant="primary" now={67} className="progress-sm" />
                </Col>
                <h1 className="mt-4">Add items</h1>
                <p className="mb-5 text-muted">Add items to the order</p>
              </Col>
            </Row>

            <Row>
              <Col xs={12} xl={8}>
                <Card>
                  <Card.Header>
                    <h4 className="card-header-title">Products</h4>
                    Toggle Discount
                    <Form.Check
                      type="switch"
                      id="discount-toggle"
                      className="ms-2"
                      checked={isDiscountEnabled}
                      onChange={handleToggleDiscount}
                    />
                  </Card.Header>
                  <Table size="sm" className="card-table table-nowrap" responsive>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Stock</th>
                        <th>Price</th>
                        {isDiscountEnabled && <th>Selling Price</th>}
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody className="fs-base">
                      {products?.map(product => (
                        <tr key={product._id.toString()}>
                          <td>{product.name}</td>
                          <td>{product.stockAtHand}</td>
                          <td>{product?.price ? product.price.toLocaleString() : '-'}</td>
                          {isDiscountEnabled && (
                            <td>
                              <FormControl
                                size="sm"
                                value={sellingPrices[product._id.toString()] ?? product.price ?? 0}
                                onChange={e => handleSellingPriceChange(product._id.toString(), e.target.value)}
                              />
                            </td>
                          )}
                          <td>
                            <InputGroup size="sm" className="flex-nowrap">
                              <Button
                                variant="outline-secondary"
                                onClick={() => decrementQuantity(product._id.toString())}
                                disabled={(cart[product._id.toString()]?.quantity || 0) <= 0}
                              >
                                -
                              </Button>
                              <FormControl
                                size="sm"
                                type="number"
                                value={cart[product._id.toString()]?.quantity || 0}
                                onChange={e =>
                                  handleQuantityChange(product._id.toString(), parseInt(e.target.value) || 0)
                                }
                                min="0"
                                max={product.stockAtHand}
                                style={{ minWidth: '50px' }}
                              />
                              <Button
                                variant="outline-secondary"
                                onClick={() => incrementQuantity(product._id.toString())}
                                disabled={(cart[product._id.toString()]?.quantity || 0) >= product.stockAtHand}
                              >
                                +
                              </Button>
                            </InputGroup>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              </Col>
              <Col xs={12} xl={4}>
                <Card>
                  <Card.Header>
                    <h4 className="card-header-title">Cart</h4>
                    <div className="cart-container d-flex align-items-center position-relative">
                      <FeatherIcon icon="shopping-cart" size="26" />
                      <Badge bg="primary" className="position-absolute" style={{ top: '9px', right: '-8px' }}>
                        {totalProductsInCart}
                      </Badge>
                    </div>
                  </Card.Header>
                  {Object.keys(cart).length === 0 ? (
                    <Card.Body className="text-center">
                      <EmptyTableState message="No items in the cart yet" />
                    </Card.Body>
                  ) : (
                    <Table size="sm" className="card-table table-nowrap" responsive>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th></th>
                        </tr>
                      </thead>
                      {Object.keys(cart).length && (
                        <tbody className="fs-base">
                          {Object.values(cart).map(item =>
                            item?._id ? (
                              <tr key={item._id.toString()}>
                                <td>{item.name}</td>
                                <td>{item.quantity}</td>
                                <td>
                                  {item.sellingPrice !== undefined && item.sellingPrice !== item.price ? (
                                    <>
                                      <span className="text-muted text-decoration-line-through me-2">
                                        {item.price?.toLocaleString()}
                                      </span>
                                      {item.sellingPrice?.toLocaleString()}
                                    </>
                                  ) : (
                                    item.price?.toLocaleString()
                                  )}
                                </td>
                                <td>
                                  <span
                                    role="button"
                                    onClick={() => handleRemoveItem(item._id.toString())}
                                    style={{
                                      cursor: 'pointer',
                                      color: 'red'
                                    }}
                                  >
                                    <FeatherIcon icon="x" size="14" />
                                  </span>
                                </td>
                              </tr>
                            ) : null
                          )}
                        </tbody>
                      )}
                    </Table>
                  )}
                </Card>
              </Col>
            </Row>

            <Nav className="row align-items-center justify-content-between">
              <Col xs="auto">
                <Button
                  variant="white"
                  size="lg"
                  onClick={() => {
                    resetCustomerSelection();
                    setActiveStep(activeStep - 1);
                    localStorage.removeItem(PREPARED_ORDER_KEY);
                  }}
                >
                  Back
                </Button>
              </Col>

              <Col xs="auto">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={mutationLoading}
                  style={{ minWidth: '80px' }}
                >
                  {mutationLoading ? <Spinner animation="border" size="sm" /> : 'Next'}
                </Button>
              </Col>
            </Nav>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default StepTwo;
